import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, BookOpen, RotateCcw } from "lucide-react";

const ROWS = [
  { key: "sodium_mg",     label: "Sodium",     unit: "mg/day",  hint: "KDIGO: <2000 mg/day for BP + fluid control." },
  { key: "potassium_mg",  label: "Potassium",  unit: "mg/day",  hint: "CKD 3-4 typical range: 2000-3000 mg/day." },
  { key: "phosphorus_mg", label: "Phosphorus", unit: "mg/day",  hint: "800-1000 mg/day; watch phosphate additives." },
  { key: "protein_g",     label: "Protein",    unit: "g/day",   hint: "0.6-0.8 g/kg/day for non-dialysis CKD." },
  { key: "bp_systolic",   label: "BP systolic",  unit: "mmHg",  hint: "KDIGO: <120-130 mmHg." },
  { key: "bp_diastolic",  label: "BP diastolic", unit: "mmHg",  hint: "<80 mmHg." },
];

export default function PrescribedTargetsModal({ open, onOpenChange, onSaved }) {
  const [resolved, setResolved] = useState(null);
  const [prescribed, setPrescribed] = useState({});
  const [prescriber, setPrescriber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const res = await api.get("/patient/targets/resolved");
      setResolved(res.data.resolved);
      const pt = res.data.prescribed_targets || {};
      // Map into form-friendly strings (empty string = use guideline)
      const form = {};
      for (const r of ROWS) form[r.key] = pt[r.key] == null ? "" : String(pt[r.key]);
      setPrescribed(form);
      setPrescriber(pt.prescribed_by || "");
    })();
  }, [open]);

  const handleChange = (key, val) => setPrescribed((p) => ({ ...p, [key]: val }));

  const handleReset = (key) => setPrescribed((p) => ({ ...p, [key]: "" }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { prescribed_by: prescriber || null };
      for (const r of ROWS) {
        const raw = prescribed[r.key];
        if (raw === "" || raw == null) {
          payload[r.key] = null;
        } else {
          const n = parseInt(raw, 10);
          if (Number.isFinite(n)) payload[r.key] = n;
        }
      }
      const res = await api.put("/patient/targets", payload);
      toast.success("Prescribed targets updated");
      setResolved(res.data.resolved);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save targets");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121514] border-[#272E2B] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="targets-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#4ADE80]" />
            Prescribed Targets
          </DialogTitle>
          <DialogDescription className="text-[#A3ACA7]">
            Doctor-prescribed targets override the general KDIGO guidelines. Leave a field blank to
            fall back to the guideline value.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {ROWS.map((r) => {
            const resKey = r.key;
            const info = resolved?.[resKey];
            const isPrescribed = info?.source === "prescribed";
            return (
              <div key={r.key} className="ros-card-raised" data-testid={`targets-row-${r.key}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-white">{r.label}</div>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                          isPrescribed
                            ? "border-[#4ADE80]/40 text-[#4ADE80] bg-[#4ADE80]/10"
                            : "border-[#272E2B] text-[#A3ACA7]"
                        }`}
                      >
                        {isPrescribed ? <ShieldCheck size={10} /> : <BookOpen size={10} />}
                        {isPrescribed ? "Prescribed" : "Guideline"}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#A3ACA7] mt-1">{r.hint}</div>
                    {info && (
                      <div className="text-[10px] text-[#A3ACA7] mt-1">
                        Active: <span className="text-white font-semibold">{info.value} {r.unit}</span>
                        {isPrescribed && info.guideline_value !== info.value && (
                          <span className="ml-2 text-[#A3ACA7]">
                            (guideline: {info.guideline_value})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder={info ? `${info.guideline_value}` : ""}
                      value={prescribed[r.key] ?? ""}
                      onChange={(e) => handleChange(r.key, e.target.value)}
                      data-testid={`targets-input-${r.key}`}
                      className="w-28 bg-[#1C201E] border-[#272E2B]"
                    />
                    <button
                      onClick={() => handleReset(r.key)}
                      title="Reset to guideline"
                      data-testid={`targets-reset-${r.key}`}
                      className="text-[#A3ACA7] hover:text-white p-1"
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          <div>
            <Label className="ros-label" htmlFor="prescribed-by">Prescribed by</Label>
            <Input
              id="prescribed-by"
              placeholder="e.g. Dr. Patel, Nephrology"
              value={prescriber}
              onChange={(e) => setPrescriber(e.target.value)}
              data-testid="targets-prescribed-by"
              className="mt-2 bg-[#1C201E] border-[#272E2B]"
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-full border-[#272E2B] hover:bg-[#1C201E]">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="targets-save-btn"
                  className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold">
            {saving ? "Saving…" : "Save targets"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
